import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useVoiceStore } from '../../stores/voiceStore'
import { mediaUrl } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'

function formatTime(ts: number) {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

// One rendered bubble — already aggregated across consecutive bursts
// from the same speaker (see merge logic below). `endTimestamp` is the
// wall-clock time of the last word in the bubble; it's what we measure
// the next burst's gap against (gap-from-last-word, NOT
// gap-from-first-word).
type Bubble = {
  key: string
  speakerId: string | null
  speakerName: string
  avatarUrl: string | null
  // OSPROD-295: accumulate the pieces and join once. Rebuilding `text` per
  // appended utterance was O(n^2), and this runs inside a useMemo that
  // re-fires on every live partial — several times a second while someone
  // talks. On a long monologue that was tens of MB of string copying.
  parts: string[]
  text: string
  timestamp: number
  endTimestamp: number
  isLive: boolean
}

// Within a single speaker's bubble, two consecutive bursts get joined
// with a regular space if the silence between them was shorter than
// this — a continuous thought reads as one paragraph. Past this
// threshold the bursts are joined with a paragraph break (`\n\n`)
// inside the SAME bubble; same-speaker text never splits into two
// bubbles, only a different speaker forces a new bubble.
const PARAGRAPH_BREAK_MS = 4000

// When the server hasn't told us when a burst ended (legacy sessions
// without endTimestamp on the wire), fall back to a length-based
// estimate. Russian/English speaking pace is ~80 ms per character; we
// undershoot a bit at 60 ms so the next-bubble gap math errs toward
// "merge" rather than "split" — splits are the user-reported pain.
const ESTIMATED_MS_PER_CHAR = 60

// Stable colour-from-hash so each speaker gets a consistent name colour
// across the panel even before their avatar loads. Same palette as the
// member-list / DM chips so the user sees a familiar identity hue.
function colourForSpeaker(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  const palette = [
    'text-emerald-400',
    'text-sky-400',
    'text-violet-400',
    'text-rose-400',
    'text-amber-400',
    'text-cyan-400',
    'text-fuchsia-400',
    'text-lime-400',
  ]
  return palette[Math.abs(h) % palette.length]
}

export default function TranscriptionPanel() {
  const transcripts = useVoiceStore((s) => s.transcripts)
  const livePartials = useVoiceStore((s) => s.livePartials)
  const participants = useVoiceStore((s) => s.participants)
  const transcriptsTotal = useVoiceStore((s) => s.transcriptsTotal)
  const transcriptsHasMore = useVoiceStore((s) => s.transcriptsHasMore)
  const transcriptsLoadingOlder = useVoiceStore((s) => s.transcriptsLoadingOlder)
  const requestOlderTranscripts = useVoiceStore((s) => s.requestOlderTranscripts)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Pinned-to-bottom by default; flips false the moment the user scrolls
  // up to read older entries, flips back on when they scroll near the
  // bottom again. Without this, smooth-scrolling on every new entry
  // yanked the viewport away from anything older than the live tail.
  const stickToBottomRef = useRef(true)

  // Avatar lookup keyed by speakerId. The voice room's `participants`
  // array carries `{ userId, user: { avatarUrl, ... } }` for every
  // member that's actually in the room. Falls back to null (initial
  // letter circle in render) for guests + speakers whose participant
  // row hasn't been hydrated yet.
  const avatarBySpeaker = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const p of participants as any[]) {
      const u = p?.user
      map.set(p.userId, u?.avatarUrl ?? null)
    }
    return map
  }, [participants])

  // Build the bubble list. Same rule applies to committed-with-
  // committed AND live-into-prior-committed merges from the same
  // speaker: if the new burst started within GROUP_GAP_MS of the
  // previous burst's LAST word (not first word), fold it into the
  // existing bubble. The user reported the start-time math was
  // splitting "Может надо сделать какой-то моно сделать. Чтобы было у
  // всех" / "Хотя" into two bubbles even though Pavel only paused for
  // ~1 s after finishing the first sentence.
  const bubbles: Bubble[] = useMemo(() => {
    const endOf = (start: number, end: number | undefined, text: string) =>
      end ?? start + Math.min(15_000, text.length * ESTIMATED_MS_PER_CHAR)

    type Item = {
      id: string
      speakerId: string | null
      speakerName: string
      text: string
      timestamp: number
      endTimestamp?: number
      isLive: boolean
    }
    const items: Item[] = []
    for (const t of transcripts) {
      items.push({
        id: t.id,
        speakerId: t.speakerId ?? null,
        speakerName: t.speakerName,
        // Prefer the LLM-cleaned text once the worker has filled it in.
        // Bubble merging then operates on the cleaner copy.
        text: t.cleanedText ?? t.text,
        timestamp: t.timestamp,
        endTimestamp: t.endTimestamp,
        isLive: false,
      })
    }
    for (const p of Object.values(livePartials)) {
      items.push({
        id: `partial-${p.speakerId}`,
        speakerId: p.speakerId,
        speakerName: p.speakerName,
        text: p.text,
        timestamp: p.timestamp,
        endTimestamp: p.endTimestamp,
        isLive: true,
      })
    }
    items.sort((a, b) => a.timestamp - b.timestamp)

    const merged: Bubble[] = []
    for (const it of items) {
      const last = merged[merged.length - 1]
      const sameSpeaker = last
        && (it.speakerId ?? it.speakerName) === (last.speakerId ?? last.speakerName)
      // Same speaker → always one bubble. Different speaker → fresh
      // bubble. The user's rule: a new transcript only appears on
      // speaker change, not on silence — even a long pause stays in
      // the previous speaker's bubble, just rendered as a paragraph
      // break.
      if (last && sameSpeaker) {
        const lastEnd = endOf(last.timestamp, last.endTimestamp, last.text)
        const gap = it.timestamp - lastEnd
        const separator = gap > PARAGRAPH_BREAK_MS ? '\n' : ' '
        last.parts.push(separator, it.text)
        last.timestamp = Math.min(last.timestamp, it.timestamp)
        last.endTimestamp = endOf(it.timestamp, it.endTimestamp, it.text)
        // key stays the first utterance's id — it only has to be stable and
        // unique, and the old `key+id+id+…` accordion grew without bound.
        // Live wins: if any contributor is live, the merged bubble
        // shows the LIVE pill until silence-flush commits the lot.
        last.isLive = last.isLive || it.isLive
        continue
      }
      merged.push({
        key: it.id,
        speakerId: it.speakerId,
        speakerName: it.speakerName,
        avatarUrl: it.speakerId ? (avatarBySpeaker.get(it.speakerId) ?? null) : null,
        parts: [it.text],
        text: '',
        timestamp: it.timestamp,
        endTimestamp: endOf(it.timestamp, it.endTimestamp, it.text),
        isLive: it.isLive,
      })
    }
    for (const b of merged) b.text = b.parts.join('').trim()
    return merged
  }, [transcripts, livePartials, avatarBySpeaker])

  // OSPROD-295: pull older entries when the user scrolls near the top.
  // `pendingPrependRef` records the pre-insert geometry so the view can be
  // pinned to the same line afterwards — without it, prepending N bubbles
  // yanks the reader upward by N bubbles' worth of height.
  const pendingPrependRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null)
  const loadOlder = () => {
    const el = scrollRef.current
    if (!el || !transcriptsHasMore || transcriptsLoadingOlder) return
    pendingPrependRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop }
    // The socket emit lives in useTranscription — the store action just
    // signals intent. Keeps this component free of socket imports.
    requestOlderTranscripts()
  }

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottomRef.current = distanceFromBottom < 32
    if (el.scrollTop < 120) loadOlder()
  }

  // Restore the reading position after a prepend. useLayoutEffect so it runs
  // before paint — in a plain useEffect the jump is visible.
  useLayoutEffect(() => {
    const el = scrollRef.current
    const pending = pendingPrependRef.current
    if (!el || !pending) return
    pendingPrependRef.current = null
    el.scrollTop = el.scrollHeight - pending.scrollHeight + pending.scrollTop
  }, [transcripts.length])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || !stickToBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [bubbles.length])
  // Also re-stick when a partial's text grows — bubbles.length doesn't
  // change but the panel gets taller. Watch a digest of partial lengths.
  const partialDigest = useMemo(
    () => Object.values(livePartials).map((p) => p.text.length).join('|'),
    [livePartials]
  )
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !stickToBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [partialDigest])

  return (
    <div className="flex flex-col h-full bg-card rounded-xl ring-1 ring-border shadow-md overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center justify-between shrink-0">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Transcription</span>
        {/* OSPROD-295: the real server-side count. This used to be the length
            of the loaded array, which is why it froze at exactly 200. */}
        <span className="text-[10px] text-muted-foreground">{transcriptsTotal ?? transcripts.length} entries</span>
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        // overflow-anchor off: the browser's own scroll anchoring fights the
        // manual offset restore after a prepend.
        style={{ overflowAnchor: 'none' }}
        className="flex-1 overflow-y-auto p-3 space-y-2.5 scrollbar-thin"
      >
        {transcriptsLoadingOlder && (
          <div className="text-[10px] text-muted-foreground text-center py-1">Loading earlier lines…</div>
        )}
        {!transcriptsLoadingOlder && transcriptsHasMore && (
          <button
            onClick={loadOlder}
            className="w-full text-[10px] text-primary hover:underline py-1"
          >
            Load earlier lines
          </button>
        )}
        {bubbles.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-4">
            Speech will appear here when participants talk
          </div>
        )}
        {bubbles.map((b) => {
          const initial = displayInitial(b.speakerName)
          const nameColour = colourForSpeaker(b.speakerId ?? b.speakerName)
          return (
            <div key={b.key} className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 bg-muted flex items-center justify-center text-foreground/80 text-sm font-semibold">
                {b.avatarUrl ? (
                  <img src={mediaUrl(b.avatarUrl)} alt="" className="w-full h-full object-cover" />
                ) : (
                  initial
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 leading-none">
                  <span className={`text-sm font-semibold ${nameColour}`}>{b.speakerName}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{formatTime(b.timestamp)}</span>
                  {b.isLive && (
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      live
                    </span>
                  )}
                </div>
                <p className={`text-sm leading-snug mt-0.5 whitespace-pre-wrap ${b.isLive ? 'text-foreground/70 italic' : 'text-foreground'}`}>
                  {b.text}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
